import clsx from "clsx";

import { useCreateJob } from "@/hooks/useSession";
import { useSuggestions } from "@/hooks/useSuggestions";
import { useStore } from "@/lib/store";

import { Hint } from "./Hint";
import { MessageForm } from "./MessageForm";

const ASCII_LOGO = `
███╗   ███╗ █████╗ ██╗  ██╗ ██████╗
████╗ ████║██╔══██╗██║ ██╔╝██╔═══██╗
██╔████╔██║███████║█████╔╝ ██║   ██║
██║╚██╔╝██║██╔══██║██╔═██╗ ██║   ██║
██║ ╚═╝ ██║██║  ██║██║  ██╗╚██████╔╝
╚═╝     ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝
`.trim();

export function WelcomeView(): JSX.Element {
  const createJob = useCreateJob();
  const mode = useStore((state) => state.composerMode);
  const modelId = useStore((state) => state.composerModelId);
  const theme = useStore((state) => state.theme);
  const { data: suggestionData, isLoading: suggestionsLoading } = useSuggestions(4);
  const suggestions = suggestionData?.suggestions ?? [];

  async function handleSuggestion(message: string): Promise<void> {
    if (createJob.isPending) return;
    await createJob.mutateAsync({ message, mode, modelId: modelId ?? undefined });
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center p-8">
      {/* Logo and tagline - narrower */}
      <div className="mb-6 max-w-md text-center">
        <pre className="text-glow-cyan mb-4 text-[6px] leading-tight text-cyber-cyan sm:text-[8px] md:text-xs">
          {ASCII_LOGO}
        </pre>
        <p className="text-sm text-cyber-muted">Your AI operations agent.</p>
      </div>

      {/* Input - wider */}
      <div className="w-full max-w-2xl">
        <MessageForm autoFocus />
      </div>

      {/* Suggestions - below input */}
      {(suggestionsLoading || suggestions.length > 0) && (
        <div className="mt-6 w-full max-w-xl text-center">
          <div className="mb-3 text-[10px] uppercase tracking-widest text-cyber-muted/50">
            Try one of these
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {suggestionsLoading && suggestions.length === 0 ? (
              <div className="text-xs text-cyber-muted">
                <span className="animate-spin-glow mr-2 inline-block">◐</span>
                Loading suggestions...
              </div>
            ) : (
              suggestions.map((s) => (
                <Hint key={s.id} content={s.message} wrapperClassName="w-full sm:w-64">
                  <button
                    onClick={() => handleSuggestion(s.message)}
                    className={clsx(
                      "w-full truncate rounded-full px-3 py-1.5 text-xs text-cyber-text/80 transition-all hover:text-cyber-cyan",
                      theme === "base"
                        ? "bg-cyber-border/40 hover:bg-cyber-border/50"
                        : "bg-cyber-surface/60 hover:bg-cyber-surface/80",
                    )}
                    disabled={createJob.isPending}
                    aria-label={s.message}
                  >
                    {s.message}
                  </button>
                </Hint>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
